import { useCallback, useEffect, useRef } from 'react'
import { isSessionStorageAvailable } from '@moonpig/web-core-utils'
import type { ProductInfoProduct } from '../../types'

type HistoryState = {
  productId?: string
  productModal?: boolean
}

type Props = {
  canNavigateInModal: boolean
  onRestoreProduct: (product: ProductInfoProduct) => void
  onLeaveModal: () => void
}

const setSessionProductId = (productId: string) => {
  if (isSessionStorageAvailable()) {
    window.sessionStorage.setItem('mnpg_product_id', productId)
  }
}

const clearSessionProductId = () => {
  if (isSessionStorageAvailable()) {
    window.sessionStorage.removeItem('mnpg_product_id')
  }
}

export const useProductModalHistory = ({
  canNavigateInModal,
  onRestoreProduct,
  onLeaveModal,
}: Props) => {
  const viewedProducts = useRef(new Map<string, ProductInfoProduct>())
  const modalEntryCount = useRef(0)
  const isClosing = useRef(false)

  const pushProduct = useCallback(
    (product: ProductInfoProduct) => {
      viewedProducts.current.set(product.id, product)
      setSessionProductId(product.id)

      const url = new URL(window.location.href)
      url.searchParams.set('productId', product.id)

      const nextHistoryState = {
        ...(window.history.state || {}),
        productId: product.id,
        ...(canNavigateInModal ? { productModal: true } : {}),
      }

      window.history.pushState(nextHistoryState, '', url.toString())
      modalEntryCount.current += 1
    },
    [canNavigateInModal],
  )

  const closeHistory = useCallback(() => {
    clearSessionProductId()
    viewedProducts.current.clear()

    const entriesToRemove = Math.max(modalEntryCount.current, 1)
    modalEntryCount.current = 0
    isClosing.current = true

    if (canNavigateInModal) {
      window.history.go(-entriesToRemove)
    } else {
      window.history.back()
    }
  }, [canNavigateInModal])

  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      if (isClosing.current || modalEntryCount.current > 0) {
        e.stopImmediatePropagation()
      }

      if (isClosing.current) {
        isClosing.current = false
        return
      }

      const productId = new URL(window.location.href).searchParams.get(
        'productId',
      )

      if (!canNavigateInModal) {
        const sessionProductId = isSessionStorageAvailable()
          ? window.sessionStorage.getItem('mnpg_product_id')
          : ''

        if (!productId || productId !== sessionProductId) {
          viewedProducts.current.clear()
          modalEntryCount.current = 0
          onLeaveModal()
        }

        return
      }

      const historyState = window.history.state as HistoryState | null
      const restoredProduct = productId
        ? viewedProducts.current.get(productId)
        : undefined

      if (historyState?.productModal && restoredProduct) {
        setSessionProductId(restoredProduct.id)
        modalEntryCount.current = Math.max(modalEntryCount.current - 1, 1)
        onRestoreProduct(restoredProduct)
        return
      }

      clearSessionProductId()
      viewedProducts.current.clear()
      modalEntryCount.current = 0
      onLeaveModal()
    }

    window.addEventListener('popstate', onPopState, { capture: true })

    return () => {
      window.removeEventListener('popstate', onPopState, { capture: true })
    }
  }, [canNavigateInModal, onLeaveModal, onRestoreProduct])

  return {
    pushProduct,
    closeHistory,
  }
}
